import { isUndefined, omitBy } from 'lodash-es'

import { getWorkspaceToken } from './tokens'

enum HTTPMethod {
  CONNECT = 'CONNECT',
  DELETE = 'DELETE',
  GET = 'GET',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
  TRACE = 'TRACE',
}

type RequestOptions = {
  url: string
  method: HTTPMethod
  // @ts-expect-error — simplifies things downstream
  data?: Record<unknown, unknown>
  files?: Record<string, File | undefined>
}

type ApiResponse<Data = unknown> = {
  status: 'ok' | 'error'
  p: number
  total_count: number
  size: number
  offset: number
  data: Data
}

type ApiError = {
  code: string
  message: string
  status: 'error'
}

type ErrorWithApiCause = Error & { cause: ApiError }

const isErrorWithApiCause = (error: unknown): error is ErrorWithApiCause => {
  return isError(error) && isApiError(error.cause)
}

const isError = (error: unknown): error is Error => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  )
}

const isApiError = (apiError: unknown): apiError is ApiError => {
  return (
    typeof apiError === 'object' &&
    apiError !== null &&
    'message' in apiError &&
    'code' in apiError &&
    'status' in apiError &&
    typeof apiError.message === 'string'
  )
}

const performRequest = async (
  options: RequestOptions & { bearerToken?: string }
) => {
  const fetchOptions = {
    method: options.method,
    credentials: 'include',
    cache: 'no-cache',
    headers: new Headers(),
  } as const

  if (options.bearerToken) {
    fetchOptions.headers.set('Authorization', `Bearer ${options.bearerToken}`)
  }

  const cleanData = omitBy(options.data, isUndefined)
  const url = new URL(options.url, window.location.origin)

  let response
  if (['HEAD', 'GET'].includes(options.method)) {
    response = await fetch(`${url.href}?${new URLSearchParams(cleanData)}`, {
      ...fetchOptions,
    })
  } else {
    const formData = new FormData()

    Object.entries(cleanData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(key, v))
      } else {
        formData.append(key, value)
      }
    })
    if (options.files) {
      Object.entries(options.files).forEach(([key, file]) => {
        if (file) {
          formData.append(key, file)
        }
      })
    }
    response = await fetch(url.href, {
      body: formData,
      ...fetchOptions,
    })
  }

  if (response.status >= 400 && response.status < 600) {
    const apiError = (await response.json()) as ApiError
    throw new Error(apiError.message, { cause: apiError })
  }

  try {
    return await response.json()
  } catch (e) {
    throw new Error('Failed to parse response')
  }
}

const request = async <ResponseData extends ApiResponse>(
  options: RequestOptions
): Promise<ResponseData> => {
  try {
    const tokens = await getWorkspaceToken()
    return await performRequest({ bearerToken: tokens, ...options })
  } catch (e) {
    // client isn't authenticated, which is fine for speakers, headless and audience
  }
  return performRequest(options)
}

export {
  request,
  performRequest,
  isErrorWithApiCause,
  type ApiResponse,
  HTTPMethod,
}
